import React, { useState } from "react";
import { streamSSE } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { Gavel, Loader2, X, ClipboardCheck } from "lucide-react";

export default function ReviewerSimulator({ open, onClose, docType, productType, deviceName, intendedUse, content }) {
  const [output, setOutput] = useState("");
  const [meta, setMeta] = useState(null);
  const [streaming, setStreaming] = useState(false);

  const run = async () => {
    if (!content || content.trim().length < 30) {
      toast.error("Draft is too short to simulate a reviewer response.");
      return;
    }
    setStreaming(true);
    setOutput("");
    setMeta(null);
    let buf = "";
    try {
      await streamSSE(
        "/reviewer/simulate",
        {
          doc_type: docType,
          product_type: productType,
          device_name: deviceName,
          intended_use: intendedUse,
          content,
        },
        (frame) => {
          if (frame.agency || frame.doc_type_label) setMeta((m) => ({ ...(m || {}), ...frame }));
          if (frame.delta) {
            buf += frame.delta;
            setOutput(buf);
          }
          if (frame.error) toast.error(frame.error);
        }
      );
    } catch (e) {
      toast.error("Reviewer sim failed: " + e.message);
    } finally {
      setStreaming(false);
    }
  };

  if (!open) return null;
  return (
    <div data-testid="reviewer-sim-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
      <div className="flex h-[90vh] w-full max-w-5xl flex-col border border-slate-200 bg-[#FAFAF7]">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center border border-[#2563EB]/50 bg-[#2563EB]/10">
              <Gavel className="h-5 w-5 text-[#2563EB]" strokeWidth={1.6} />
            </div>
            <div>
              <div className="overline text-[#2563EB]">REVIEWER SIMULATOR</div>
              <h2 className="font-serif text-lg font-semibold text-slate-900">
                {meta?.agency ? `${meta.agency} · ${meta.doc_type_label || docType}` : `FDA Reviewer · ${docType}`}
              </h2>
            </div>
          </div>
          <button data-testid="reviewer-sim-close" onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
          <div className="border-b border-slate-200 p-4 text-xs text-slate-500 lg:w-64 lg:shrink-0 lg:border-b-0 lg:border-r">
            <div className="overline mb-2 text-slate-400">SUBJECT</div>
            <div className="font-medium text-slate-800">{deviceName || "(unnamed)"}</div>
            <div className="mt-1 leading-relaxed text-slate-500">{intendedUse || "(no intended use provided)"}</div>
            <div className="mt-6 overline text-slate-400">DRAFT LENGTH</div>
            <div className="mt-1 font-mono text-xs text-slate-600">{(content || "").length.toLocaleString()} chars</div>

            <button
              data-testid="reviewer-sim-run"
              onClick={run}
              disabled={streaming}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#1D4ED8] disabled:opacity-40"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
              {output ? "Re-run reviewer" : "Simulate reviewer"}
            </button>

            <div className="mt-4 text-[11px] text-slate-400">
              Chuck & Claude will role-play a senior FDA reviewer and produce section-by-section deficiencies and a readiness verdict.
            </div>
          </div>

          <div data-testid="reviewer-sim-output" className="flex-1 overflow-y-auto p-6">
            {output ? (
              <Markdown>{output}</Markdown>
            ) : (
              <div className="flex h-full items-center justify-center text-center text-slate-400">
                <div>
                  <Gavel className="mx-auto mb-3 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="text-sm">Hit <b className="text-slate-600">Simulate reviewer</b> to get a section-by-section deficiency read.</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
